import { toAmericanDate, toReadableTime } from "../../../utils/helpers";
import React from "react";
import { useTranslation } from "react-i18next";
import FileAttachmentFieldForDownload from "./components/FileAttachmentFieldForDownload/FileAttachmentFieldForDownload";
import VideoEmbed from "../VideoEmbed";
import { Emoji } from "../../elements/Emoji/Emoji";
import { ResponseField } from "types";
import CheckboxGridResponse from "components/elements/CheckboxGridResponse";
import RangeSliderResponse from "components/elements/RangeSliderResponse";
import {
  getPatientWord,
  useOrganizationSettings,
} from "utils/hooks/organizations/useOrganizationSettings";

interface GeneratedFieldResponseProps {
  field: ResponseField;
  isTable?: boolean;
  scoreSuffix?: string;
}

const GeneratedFieldResponse = ({
  field,
  isTable,
  scoreSuffix,
}: GeneratedFieldResponseProps) => {
  const { settings: orgSettings } = useOrganizationSettings();
  const { t } = useTranslation("common");

  switch (field.fieldType) {
    case "DATE":
      return (
        <p className="single-submission-for-patients-page__field">
          {toAmericanDate(field.value)}
        </p>
      );
    case "TIME":
      return (
        <p className="single-submission-for-patients-page__field">
          {toReadableTime(field.value)}
        </p>
      );
    case "INPUT_LIST": {
      const values = JSON.parse(field.value || "[]");
      return (
        <ol>
          {values.map((item: string, index: number) => (
            <li key={index + 1}>{item}</li>
          ))}
        </ol>
      );
    }
    case "MULTI_VALUE": {
      const values = JSON.parse(field.value || "{}");
      return <p>{Object.values(values).join(" ")}</p>;
    }
    case "CHECKBOX_GRID": {
      return (
        <CheckboxGridResponse
          choices={field.value}
          layout={isTable ? "horizontal" : "vertical"}
        />
      );
    }
    case "SCALE": {
      return (
        <RangeSliderResponse
          value={field.value}
          showScore={field.includeInScoreCalculation}
          scoreSuffix={scoreSuffix}
          scoreAsPercentage={scoreSuffix === "%"}
          min={field.min}
          max={field.max}
        />
      );
    }
    case "HEADING_FIELD": {
      return null;
    }
    case "PARAGRAPH_FIELD": {
      return null;
    }
    case "INFO_BOX": {
      return null;
    }
    case "MULTI_RESPONSE": {
      const values = JSON.parse(field.value || "{}");
      return (
        <>
          <p>
            {Object.keys(values).map((key, index) => {
              return (
                <span className="flex row" key={index}>
                  <span className="mr-05">{index + 1}.</span>
                  <span>{values[key]}</span>
                </span>
              );
            })}
          </p>
        </>
      );
    }
    case "FILE_ATTACHMENT": {
      return (
        <div>
          <p>
            <b>
              {t("resources.file_shared", {
                patientWord: getPatientWord(orgSettings),
              })}
            </b>
          </p>
          <FileAttachmentFieldForDownload
            uuid={field.uuid}
            fileId={field.value}
          />
        </div>
      );
    }
    case "VIDEO_EMBED": {
      return (
        <div>
          <p>
            <b>Video{field.videoURL}:</b>
          </p>
          <VideoEmbed
            videoURL={field.videoURL || field.value}
            uuid={field.uuid}
          />
        </div>
      );
    }
    case "MOOD_SCALE":
    case "CUSTOM_MOOD_SCALE": {
      let emojiData;

      if (typeof field.value === "string" && field.value.trim() !== "") {
        try {
          const parsed = JSON.parse(field.value);

          if (typeof parsed === "object" && parsed !== null) {
            emojiData = parsed;
          } else {
            return <p>{field.value}</p>;
          }
        } catch (error) {
          return <p>{field.value}</p>;
        }
      } else if (typeof field.value === "object" && field.value !== null) {
        emojiData = field.value;
      } else {
        return <div>No mood data available</div>;
      }

      // Ensure emojiData is an object (extra safety)
      if (typeof emojiData !== "object" || emojiData === null) {
        return <p>{field.value}</p>;
      }

      return <Emoji emoji={emojiData} />;
    }
    default:
      return (
        <p className="single-submission-for-patients-page__field">
          {field.value}
        </p>
      );
  }
};

export default GeneratedFieldResponse;
